import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, CircularProgress, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { HonorariosService } from '../services/honorariosService';
import { PlanillonService } from '../../planillon/services/planillonService';
import type { ReciboHonorarios } from '../types/honorarios';
import type { PlanillaCabecera } from '../../planillon/types/planillon';

const fmt = (n: number) => `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyRecibo: ReciboHonorarios = { idRecibo: 0, idPeriodo: 0, razonSocialLocador: '', rucLocador: '', serieNumero: null, fechaEmision: new Date().toISOString().split('T')[0], montoHonorario: 0, retencion4ta: 0, netoPagar: 0, descripcion: null, estado: 'PENDIENTE' };

export default function HonorariosPage() {
  const [recibos, setRecibos] = useState<ReciboHonorarios[]>([]);
  const [planillas, setPlanillas] = useState<PlanillaCabecera[]>([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<ReciboHonorarios>(emptyRecibo);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); loadPlanillas(); }, []);
  const load = async () => { try { setRecibos(await HonorariosService.getAll()); } catch { /* handled */ } };
  const loadPlanillas = async () => { try { setPlanillas(await PlanillonService.getAll()); } catch { /* handled */ } };

  const handleSave = async () => {
    setLoading(true);
    try { await HonorariosService.save(form); setDialog(false); await load(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar recibo?')) return;
    try { await HonorariosService.delete(id); await load(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'serieNumero', headerName: 'Serie-Número', width: 130 },
    { field: 'razonSocialLocador', headerName: 'Locador', flex: 1, minWidth: 200 },
    { field: 'rucLocador', headerName: 'RUC', width: 120 },
    { field: 'fechaEmision', headerName: 'F. Emisión', width: 110, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'montoHonorario', headerName: 'Monto', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'retencion4ta', headerName: 'Ret. 8%', width: 100, valueFormatter: (v) => fmt(v) },
    { field: 'netoPagar', headerName: 'Neto', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (p) => <Chip label={p.value} size="small" color={p.value === 'PAGADO' ? 'success' : p.value === 'ANULADO' ? 'error' : 'default'} /> },
    { field: 'acciones', headerName: '', width: 120, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => { setForm(p.row); setDialog(true); }}><EditIcon fontSize="small" /></Button>
          <Button size="small" color="error" onClick={() => handleDelete(p.row.idRecibo)}><DeleteIcon fontSize="small" /></Button>
        </Stack>
      )},
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Recibos por Honorarios (4ta Categoría)</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...emptyRecibo, idPeriodo: planillas[0]?.idPeriodo || 0 }); setDialog(true); }}>Nuevo Recibo</Button>
      </Stack>
      <DataGrid rows={recibos} columns={columns} getRowId={(row) => row.idRecibo} autoHeight density="compact" disableRowSelectionOnClick pageSizeOptions={[25]} />

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.idRecibo ? 'Editar Recibo' : 'Nuevo Recibo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Periodo</InputLabel>
              <Select value={form.idPeriodo || ''} label="Periodo" onChange={(e) => setForm({ ...form, idPeriodo: Number(e.target.value) })}>
                {planillas.map(p => <MenuItem key={p.idPlanilla} value={p.idPeriodo}>{p.descripcion}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Razón Social Locador" fullWidth value={form.razonSocialLocador} onChange={(e) => setForm({ ...form, razonSocialLocador: e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField label="RUC" fullWidth value={form.rucLocador} onChange={(e) => setForm({ ...form, rucLocador: e.target.value })} />
              <TextField label="Serie-Número" fullWidth value={form.serieNumero ?? ''} onChange={(e) => setForm({ ...form, serieNumero: e.target.value || null })} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Fecha Emisión" type="date" fullWidth value={form.fechaEmision?.split('T')[0] ?? ''} onChange={(e) => setForm({ ...form, fechaEmision: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Monto Honorario (S/)" type="number" fullWidth value={form.montoHonorario || ''} onChange={(e) => setForm({ ...form, montoHonorario: Number(e.target.value) })} />
            </Stack>
            <TextField label="Descripción" fullWidth multiline rows={2} value={form.descripcion ?? ''} onChange={(e) => setForm({ ...form, descripcion: e.target.value || null })} />
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={form.estado} label="Estado" onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                <MenuItem value="PAGADO">Pagado</MenuItem>
                <MenuItem value="ANULADO">Anulado</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{loading ? <CircularProgress size={18} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
